import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { SiteLayout } from "@/components/SiteLayout";
import { Section } from "@/components/Section";
import { PageHero } from "@/components/PageHero";
import { PublicMealPlanConfigurator } from "@/components/mp2/PublicMealPlanConfigurator";
import { usePublicMp2Catalog } from "@/hooks/usePublicMp2Catalog";
import { MealPlanProductPage } from "@/components/mp2/MealPlanProductPage";

const searchSchema = z.object({ plan: z.string().min(1).max(80).optional() });

export const Route = createFileRoute("/meal-plans_/configure")({
  validateSearch: (raw) => searchSchema.parse(raw),
  head: () => ({
    meta: [
      { title: "Configure your meal plan — Poonam Kapur Healthy Kitchen" },
      {
        name: "description",
        content:
          "Configure your subscription meal plan — pick combination, food preference, duration, delivery schedule and area, then pay securely.",
      },
      { name: "robots", content: "noindex, nofollow" },
      { property: "og:title", content: "Configure your meal plan — Poonam Kapur Healthy Kitchen" },
      {
        property: "og:description",
        content: "Build your subscription in a few steps and pay securely with Razorpay.",
      },
    ],
  }),
  component: ConfigurePage,
});

function ConfigurePage() {
  const { plan: planSlug } = Route.useSearch();
  const { catalog, isMp2Visible, isLoading } = usePublicMp2Catalog();

  if (isLoading) return (
    <SiteLayout>
      <PageHero eyebrow="Meal Plans" title="Loading..." intro="" />
      <Section><div className="text-muted-foreground text-center py-12">Loading catalog...</div></Section>
    </SiteLayout>
  );

  if (!isMp2Visible || !catalog) return (
    <SiteLayout>
      <PageHero eyebrow="Meal Plans" title="Coming Soon" intro="Our new subscription plans are launching shortly." />
    </SiteLayout>
  );

  const plan = planSlug ? catalog.plans.find(p => p.slug === planSlug) : catalog.plans[0];

  if (!plan) return (
    <SiteLayout>
      <PageHero eyebrow="Meal Plans" title="Plan not found" intro="The requested plan could not be located." />
    </SiteLayout>
  );

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Meal Plans"
        title={plan.title}
        intro={plan.tagline ?? plan.description ?? "Configure your subscription below."}
      />
      <Section>
        <MealPlanProductPage plan={plan} />
      </Section>
    </SiteLayout>
  );
}
